/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from "react";
import { addDays, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Package, ArrowClockwise } from "@phosphor-icons/react";
import { pedidosApi } from "../../page/OrderResumePag/_services/api";
import CepModal from "./CepModal";
import {
  ShippingSection,
  ShippingTitle,
  ShippingBox,
  ShippingBoxError,
  ShippingInfoRow,
  ShippingInfoText,
  ShippingText,
  ShippingAddress,
  ChangeAddressLink,
  ReloadButton,
} from "./styles";

function formatAddress(zipCode, userAddress) {
  if (userAddress && userAddress.logradouro) {
    const parts = [
      userAddress.logradouro,
      userAddress.bairro,
      userAddress.cidade,
      userAddress.uf,
    ].filter(Boolean);
    return parts.join(" - ");
  }

  const cleaned = zipCode.replace(/\D/g, "");
  if (cleaned.length === 8) {
    return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}-${cleaned.slice(5)}`;
  }
  return zipCode;
}

function ShippingEstimate({ sku, userCep, userAddress, userUuid, onCartAdded, displayMode }) {
  const [shippingState, setShippingState] = useState("idle");
  const [shippingData, setShippingData] = useState({});
  const [cep, setCep] = useState("");
  const [showCepModal, setShowCepModal] = useState(false);

  const calculateShipping = useCallback(
    async (zipCode) => {
      setShippingState("loading");
      setCep(zipCode);

      try {
        const channel = process.env.REACT_APP_DEFAULT_CHANNEL;
        const configData = JSON.parse(localStorage.getItem("config") || "{}");
        const fatorFrete = configData[`fator_${channel}_frete`] / 10000 || 0;

        const response = await pedidosApi.post(`v2/carrinho/${channel}`, {
          customerUuid: userUuid,
          cep: zipCode,
          sku,
          quantity: 1,
          fator: fatorFrete,
        });

        // Extract delivery options from first item
        const items = response.data.data.items;
        const firstItemKey = Object.keys(items)[0];
        const deliveryOptions = items[firstItemKey]?.deliveryOptions;

        if (
          !deliveryOptions ||
          deliveryOptions.shippingPrice === null ||
          deliveryOptions.shippingPrice === undefined
        ) {
          setShippingState("no-delivery");
          setShippingData({ address: formatAddress(zipCode, userAddress) });
          return;
        }

        setShippingData({
          price: deliveryOptions.shippingPrice,
          points: deliveryOptions.shippingPoints,
          estimatedDays: deliveryOptions.estimatedBusinessDays,
          address: formatAddress(zipCode, userAddress),
        });
        setShippingState("calculated");
        onCartAdded?.(true);
      } catch (error) {
        console.error("Erro ao calcular frete:", error);
        setShippingState("error");
        onCartAdded?.(false);
      }
    },
    [sku, userUuid, userAddress, onCartAdded]
  );

  // On mount: if userCep exists, auto-calculate
  useEffect(() => {
    if (userCep) {
      calculateShipping(userCep);
    }
    // eslint-disable-next-line
  }, []);

  // When sku changes: recalculate if cep exists, otherwise reset
  useEffect(() => {
    if (cep) {
      calculateShipping(cep);
    } else {
      setShippingState("idle");
    }
    // eslint-disable-next-line
  }, [sku]);

  const renderContent = () => {
    switch (shippingState) {
      case "idle":
        return (
          <ShippingBox
            onClick={() => setShowCepModal(true)}
            style={{ cursor: "pointer" }}
          >
            <Package size={20} color="#00003C" weight="regular" />
            <ShippingText>Informe um CEP</ShippingText>
          </ShippingBox>
        );

      case "loading":
        return (
          <ShippingBox>
            <Package size={20} color="#00003C" weight="regular" />
            <ShippingText>Calculando...</ShippingText>
          </ShippingBox>
        );

      case "calculated": {
        const estimatedDate = addDays(
          new Date(),
          shippingData.estimatedDays || 0
        );
        const formattedDate = format(estimatedDate, "d 'de' MMM", {
          locale: ptBR,
        });

        const formatShippingValue = () => {
          const money = shippingData.price || 0;
          const points = shippingData.points || 0;
          const moneyFormatted = `R$ ${money.toFixed(2).replace(".", ",")}`;
          const pointsFormatted = `${Math.floor(points).toLocaleString("pt-BR")} pts`;

          if (displayMode === "points") return pointsFormatted;
          if (displayMode === "both") return `${moneyFormatted} / ${pointsFormatted}`;
          return moneyFormatted;
        };

        return (
          <>
            <ShippingBox>
              <ShippingInfoRow>
                <Package size={20} color="#00003C" weight="regular" />
                <ShippingInfoText>
                  Chega até dia {formattedDate} • {formatShippingValue()}
                </ShippingInfoText>
              </ShippingInfoRow>
              <ShippingAddress>{shippingData.address}</ShippingAddress>
            </ShippingBox>
            <ChangeAddressLink onClick={() => setShowCepModal(true)}>
              Alterar endereço
            </ChangeAddressLink>
          </>
        );
      }

      case "no-delivery":
        return (
          <ShippingBoxError>
            <ShippingInfoRow>
              <Package size={20} color="#00003C" weight="regular" />
              <ShippingInfoText>
                Não realizamos entrega no CEP informado.
              </ShippingInfoText>
            </ShippingInfoRow>
            <ShippingAddress>{shippingData.address}</ShippingAddress>
          </ShippingBoxError>
        );

      case "error":
        return (
          <ShippingBox>
            <ShippingText>
              Ops! Não foi possível carregar seu CEP.
            </ShippingText>
            <ReloadButton onClick={() => calculateShipping(cep)}>
              <ArrowClockwise size={20} />
              Recarregar
            </ReloadButton>
          </ShippingBox>
        );

      default:
        return null;
    }
  };

  return (
    <ShippingSection>
      <ShippingTitle>Cálculo do frete</ShippingTitle>
      {renderContent()}
      <CepModal
        show={showCepModal}
        onClose={() => setShowCepModal(false)}
        onCepConfirm={(newCep, data) => {
          setCep(newCep);
          setShippingData({
            price: data.price,
            points: data.points,
            estimatedDays: data.estimatedDays,
            address: data.address,
          });
          if (data.price !== undefined && data.price !== null) {
            setShippingState("calculated");
            onCartAdded?.(true);
          } else {
            setShippingState("no-delivery");
          }
          setShowCepModal(false);
        }}
        sku={sku}
        userUuid={userUuid}
        displayMode={displayMode}
      />
    </ShippingSection>
  );
}

export default ShippingEstimate;
